<template>
  <!--
    this contains 2 child elements, one of which is the
    backdrop for the modal, and the actual modal, the backdrop
    is separated from the modal to allow a higher z-index value
    be set on the modal
  -->
  <div :class="['pa-modal-parent']" v-show="show" :aria-hidden="!show" :id="id">
    <div
      class="pa-modal"
      role="dialog"
      :aria-modal="true"
      :aria-labelledby="ariaLabelledBy"
      :aria-describedby="ariaDescribedBy"
      :style="customStyle"
      id="modal"
    >
      <!-- @slot Use this slot for the modal content -->
      <slot/>
    </div>
    <div class="pa-modal-overlay" id="modal-overlay"/>
  </div>
</template>

<script>
/**
 * @description a modal component that accepts a
 * maximum of 7 props with a blurry backdrop
 * this component is useful for dialogs that require
 * users take action, like click a button
 * for a notification, an alert dialog should
 * be used instead
 * @version 1.0.0
 */
export default {
  name: 'modal',
  data() {
    return {
      show: false,
      parentFocusableEl: document.querySelector('body'), // the last focusable element on the parent, defaults to body
      firstFocusableEl: {}, // the first focusable element in the modal body
      lastFocusableEl: {}, // the last focusable element in the modal body
      focusableEls: [], // the focusableElements in the modal body
    };
  },
  mounted() {
    // on component mount, the firstFocusEl and
    // getFocusableEls functions are run and some dom
    // changes are subscribed to

    this.subscribe();
    this.focusFirstEl();
    this.getFocusableEls();
  },
  beforeDestroy() {
    // before the component is removed from the DOM,
    // the component unsubscribed from the earlier
    // subscribed to DOM changes, for performance

    this.unsubscribe();
  },
  props: {
    /**
     * the required aria label of the modal for accessibility
     */
    ariaLabelledBy: {
      type: String,
      required: true,
      default: '',
    },
    /**
     * the required aria description of the modal for accessibility
     */
    ariaDescribedBy: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * the visibility of the modal
     */
    visible: {
      type: Boolean,
      required: true,
      default: false,
    },
    /**
     * gets called when the modal is dismissed
     */
    onDismiss: {
      type: Function,
      required: false,
      default: () => null,
    },
    /**
     * optionally add custom styled to the modal
     */
    customStyle: {
      type: String,
      required: false,
      default: '',
    },
    /**
     *  optionally hide overflow when modal is open
     */
    overflow: {
      type: String,
      required: true,
      default: 'show',
      validator(value) {
        return ['show', 'hide'].indexOf(value) !== -1;
      },
    },
    /**
     * optionally add an ID which can be useful
     * for overriding default style
     * it blows up when the ID entered is either
     * the word *modal* or *modal-overlay*
     */
    id: {
      type: String,
      required: false,
      default: `modal-${Math.floor(Math.random() * 100)}`,
      validator(value) {
        return ['modal', 'modal-overlay'].indexOf(value) === -1;
      },
    },
  },
  methods: {
    /**
     * subscribe to both click and keydown events when the component is mounted
     */
    subscribe() {
      window.addEventListener('keydown', this.handleKeyDown);
      this.$el.addEventListener('click', this.listenForOutsideClick);
    },
    /**
     * unsubscribe from both click and keydown events when the component is mounted
     */
    unsubscribe() {
      window.removeEventListener('keydown', this.handleKeyDown);
      this.$el.removeEventListener('click', this.listenForOutsideClick);
    },
    /**
     * gets called onKeyDown
     * @param {SyntheticEvent | SyntheticKeyboardEvent} e synthetic keyboard event
     */
    handleKeyDown(e) {
      const TAB_CODE = 9; // keycode for tab key
      const ESC_CODE = 27; // keycode for esc key
      const $this = this;

      /* handles forward tab navigation via keyboard */
      function handleForwardTab() {
        /**
         * checks if the active element is the same node
         * as the last focusable element in the modal, if its true,
         * the first focusable element in the modal receives focus
         */
        if (document.activeElement.isSameNode($this.lastFocusableEl)) {
          e.preventDefault();
          $this.firstFocusableEl.focus();
        }
      }

      // handles backward tab navigation via keyboard
      function handleBackwardTab() {
        /**
         * checks if the active element is the same node
         * as the first focusable element in the modal, if its true,
         * the last focusable element in the modal receives focus
         */
        if (document.activeElement.isSameNode($this.firstFocusableEl)) {
          e.preventDefault();
          $this.lastFocusableEl.focus();
        }
      }

      if (this.show) {
        switch (e.keyCode) {
          case TAB_CODE:
            if (this.focusableEls.length === 1) {
              e.preventDefault();
              break;
            }

            if (e.shiftKey) {
              handleBackwardTab();
            } else {
              handleForwardTab();
            }
            break;
          case ESC_CODE:
            if (typeof this.onDismiss === 'function') this.onDismiss();
            break;
          default:
            break;
        }
      }
    },
    /**
     *  responds to the click event and closes the modal
     *  when the outside of it is clicked
     *
     *  @param {SyntheticEvent | SyntheticMouseEvent} e synthetic mouse event
     */
    listenForOutsideClick(e) {
      if (!this.modalInstance.contains(e.target) && typeof this.onDismiss === 'function') {
        this.onDismiss();
        this.close();
      }
    },
    /* gets the active element in the document */
    getActiveEl() {
      this.parentFocusableEl = document.activeElement;
    },
    /**
     * gets called when the modal is closed
     * and focuses on the element that was active on the parent
     */
    close() {
      this.$nextTick(() => this.parentFocusableEl.focus());
    },
    /**
     * gets the focusable elements in the modal
     */
    getFocusableEls() {
      let focusableEls = this.modalInstance.querySelectorAll(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]',
      );

      focusableEls = [...focusableEls];
      const { 0: firstFocusableEl, [focusableEls.length - 1]: lastFocusableEl } = focusableEls;

      this.firstFocusableEl = firstFocusableEl;
      this.lastFocusableEl = lastFocusableEl;

      this.focusableEls = focusableEls;
    },
    /**
     * focuses on the first element in the modal,
     * when the modal is open
     */
    focusFirstEl() {
      this.$nextTick(() => this.firstFocusableEl.focus());
    },
    /**
     * method to toggle body overflow
     */
    toggleOverflow() {
      if (this.overflow === 'hide') {
        document.body.style.overflowY = this.show ? 'hidden' : 'auto';
      }
    },
  },
  computed: {
    /**
     * an instance of the modal component
     */
    modalInstance() {
      return this.$el.querySelector('#modal');
    },
    /**
     * an instance of the modal overlay component
     */
    overlayInstance() {
      return this.$el.querySelector('#modal-overlay');
    },
  },
  watch: {
    /**
     * watches for changes in the visible prop,
     * updates the show state and focuses on
     * the appropriate elements accordingly
     */
    visible(newState) {
      this.show = newState;
      this.toggleOverflow();

      if (!newState) {
        this.close();
      } else {
        this.getActiveEl();
        this.getFocusableEls();
        this.focusFirstEl();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './modal.scss';
</style>
